import { useState, useCallback } from 'react';
import { Organization, TimeZone } from 'types';
import { organizationSettingsService } from 'core/API/organizationSettings';
import {
	GetOrganizationsParams,
	GetOrganizationsResponse,
	CreateOrganizationRequest,
	UpdateOrganizationRequest,
	ApiDayPart,
} from 'types/Organization';
import { GetTimeZones } from '../API/workplaces';

interface UseOrganizationCrudState {
	organizations: Organization[];
	selectedOrganization: Organization | null;
	loading: boolean;
	error: string | null;
	meta: {
		total: number;
		page: number;
		limit: number;
	};
	organisationTypes: { id: number; name: string }[];
	brands: { label: string }[];
	timeZones: TimeZone[];
	days: {
		'@id': string;
		'@type': string;
		id: number;
		label: string;
		locale: string;
		position: string;
	}[];
	dayParts: ApiDayPart[];
}

interface UseOrganizationCrudActions {
	// Data fetching
	fetchOrganizations: (params?: GetOrganizationsParams) => Promise<void>;
	fetchOrganization: (organizationId: number) => Promise<Organization | null>;
	fetchOrganisationTypes: () => Promise<void>;
	fetchBrands: () => Promise<void>;
	fetchTimeZones: () => Promise<void>;
	fetchDays: () => Promise<void>;
	fetchDayParts: (organizationId?: number) => Promise<void>;

	// CRUD operations
	createOrganization: (orgData: CreateOrganizationRequest) => Promise<Organization | null>;
	updateOrganization: (orgData: UpdateOrganizationRequest) => Promise<Organization | null>;
	deleteOrganization: (organizationId: number) => Promise<void>;

	// Day Parts CRUD operations
	createDayPart: (
		organizationId: number,
		dayPartData: { partName: string; startTime: string; endTime: string }
	) => Promise<ApiDayPart | null>;
	updateDayPart: (
		dayPartId: number,
		dayPartData: { organization: string; partName: string; startTime: string; endTime: string }
	) => Promise<ApiDayPart | null>;
	deleteDayPart: (dayPartId: number) => Promise<void>;

	// State management
	setOrganizations: (organizations: Organization[]) => void;
	setSelectedOrganization: (organization: Organization | null) => void;
	clearError: () => void;
	resetState: () => void;
}

export interface UseOrganizationCrudReturn
	extends UseOrganizationCrudState,
		UseOrganizationCrudActions {}

/**
 * Provides full CRUD capabilities for organisations, along with supporting
 * lookup data (types, brands, time zones, days, day parts).
 *
 * All async operations update the shared loading/error state automatically.
 * Use `clearError` to dismiss errors after handling them in the UI.
 *
 * @returns A combined state-and-actions object typed as {@link UseOrganizationCrudReturn}.
 *
 * @example
 * const { organizations, fetchOrganizations, createOrganization } = useOrganizationCrud();
 * useEffect(() => { fetchOrganizations({ page: 1 }); }, []);
 */

const initialState: UseOrganizationCrudState = {
	organizations: [],
	selectedOrganization: null,
	loading: false,
	error: null,
	meta: {
		total: 0,
		page: 1,
		limit: 50,
	},
	organisationTypes: [],
	brands: [],
	timeZones: [],
	days: [],
	dayParts: [],
};

export const useOrganizationCrud = (): UseOrganizationCrudReturn => {
	const [state, setState] = useState<UseOrganizationCrudState>(initialState);

	const setLoading = useCallback((loading: boolean) => {
		setState((prev) => ({ ...prev, loading }));
	}, []);

	const setError = useCallback((error: string | null) => {
		setState((prev) => ({ ...prev, error }));
	}, []);

	const clearError = useCallback(() => {
		setState((prev) => ({ ...prev, error: null }));
	}, []);

	const setOrganizations = useCallback((organizations: Organization[]) => {
		setState((prev) => ({ ...prev, organizations: [organizations[0]] }));
	}, []);

	const setSelectedOrganization = useCallback((organization: Organization | null) => {
		setState((prev) => ({ ...prev, selectedOrganization: organization }));
	}, []);

	const resetState = useCallback(() => {
		setState(initialState);
	}, []);

	const fetchOrganizations = useCallback(
		async (params?: GetOrganizationsParams) => {
			try {
				setLoading(true);
				setError(null);

				const response: GetOrganizationsResponse =
					await organizationSettingsService.getOrganizations(params);

				setState((prev) => ({
					...prev,
					organizations: [response.data[0]],
					meta: response.meta,
					loading: false,
				}));
			} catch (err: any) {
				setError(err.message || 'Failed to fetch organizations');
				setLoading(false);
			}
		},
		[setLoading, setError]
	);

	const fetchOrganization = useCallback(
		async (organizationId: number): Promise<Organization | null> => {
			try {
				setLoading(true);
				setError(null);

				const organization = await organizationSettingsService.getOrganization(organizationId);
				setSelectedOrganization(organization);
				setLoading(false);
				return organization;
			} catch (err: any) {
				setError(err.message || 'Failed to fetch organization');
				setLoading(false);
				return null;
			}
		},
		[setLoading, setError, setSelectedOrganization]
	);

	const fetchOrganisationTypes = useCallback(async () => {
		try {
			const types = await organizationSettingsService.getOrganisationTypes();
			setState((prev) => ({ ...prev, organisationTypes: types }));
		} catch (err: any) {
			console.error('Failed to fetch organisation types:', err);
		}
	}, []);

	const fetchBrands = useCallback(async () => {
		try {
			const brands = await organizationSettingsService.getBrands();
			setState((prev) => ({ ...prev, brands: brands }));
		} catch (err: any) {
			console.error('Failed to fetch brands:', err);
		}
	}, []);

	const fetchTimeZones = useCallback(async () => {
		try {
			const zones = await GetTimeZones();
			setState((prev) => ({ ...prev, timeZones: zones }));
		} catch (err: any) {
			console.error('Failed to fetch time zones:', err);
		}
	}, []);

	const fetchDays = useCallback(async () => {
		try {
			const days = await organizationSettingsService.getDays();
			setState((prev) => ({ ...prev, days: days }));
		} catch (err: any) {
			console.error('Failed to fetch days:', err);
		}
	}, []);

	const fetchDayParts = useCallback(async (organizationId?: number) => {
		try {
			const dayParts = await organizationSettingsService.getDayParts(organizationId);
			setState((prev) => ({ ...prev, dayParts: dayParts }));
		} catch (err: any) {
			console.error('Failed to fetch day parts:', err);
		}
	}, []);

	const createDayPart = useCallback(
		async (
			organizationId: number,
			dayPartData: { partName: string; startTime: string; endTime: string }
		): Promise<ApiDayPart | null> => {
			try {
				setLoading(true);
				setError(null);

				const newDayPart = await organizationSettingsService.createDayPart({
					organization: `/api/organizations/${organizationId}`,
					...dayPartData,
				});

				// Add the new day part to the current list
				setState((prev) => ({
					...prev,
					dayParts: [...prev.dayParts, newDayPart],
					loading: false,
				}));

				return newDayPart;
			} catch (err: any) {
				setError(err.message || 'Failed to create day part');
				setLoading(false);
				return null;
			}
		},
		[setLoading, setError]
	);

	const updateDayPart = useCallback(
		async (
			dayPartId: number,
			dayPartData: { organization: string; partName: string; startTime: string; endTime: string }
		): Promise<ApiDayPart | null> => {
			try {
				setLoading(true);
				setError(null);

				const updatedDayPart = await organizationSettingsService.updateDayPart(
					dayPartId,
					dayPartData
				);

				// Update the day part in the current list
				setState((prev) => ({
					...prev,
					dayParts: prev.dayParts.map((dp) => (dp.id === dayPartId ? updatedDayPart : dp)),
					loading: false,
				}));

				return updatedDayPart;
			} catch (err: any) {
				setError(err.message || 'Failed to update day part');
				setLoading(false);
				return null;
			}
		},
		[setLoading, setError]
	);

	const deleteDayPart = useCallback(
		async (dayPartId: number): Promise<void> => {
			try {
				setLoading(true);
				setError(null);

				await organizationSettingsService.deleteDayPart(dayPartId);

				// Remove the day part from the current list
				setState((prev) => ({
					...prev,
					dayParts: prev.dayParts.filter((dp) => dp.id !== dayPartId),
					loading: false,
				}));
			} catch (err: any) {
				setError(err.message || 'Failed to delete day part');
				setLoading(false);
				throw err;
			}
		},
		[setLoading, setError]
	);

	const createOrganization = useCallback(
		async (orgData: CreateOrganizationRequest): Promise<Organization | null> => {
			try {
				setLoading(true);
				setError(null);

				const newOrganization = await organizationSettingsService.createOrganization(orgData);

				// Add the new organization to the current list
				setState((prev) => ({
					...prev,
					organizations: [...prev.organizations, newOrganization],
					meta: {
						...prev.meta,
						total: prev.meta.total + 1,
					},
					loading: false,
				}));

				return newOrganization;
			} catch (err: any) {
				setError(err.message || 'Failed to create organization');
				setLoading(false);
				return null;
			}
		},
		[setLoading, setError]
	);

	const updateOrganization = useCallback(
		async (orgData: UpdateOrganizationRequest): Promise<Organization | null> => {
			try {
				setLoading(true);
				setError(null);

				const updatedOrganization = await organizationSettingsService.updateOrganization(orgData);

				// Update the organization in the current list
				setState((prev) => ({
					...prev,
					organizations: prev.organizations.map((org) =>
						org.id === updatedOrganization.id ? updatedOrganization : org
					),
					selectedOrganization:
						prev.selectedOrganization?.id === updatedOrganization.id
							? updatedOrganization
							: prev.selectedOrganization,
					loading: false,
				}));

				return updatedOrganization;
			} catch (err: any) {
				setError(err.message || 'Failed to update organization');
				setLoading(false);
				return null;
			}
		},
		[setLoading, setError]
	);

	const deleteOrganization = useCallback(
		async (organizationId: number): Promise<void> => {
			try {
				setLoading(true);
				setError(null);

				await organizationSettingsService.deleteOrganization(organizationId);

				// Remove the organization from the current list
				setState((prev) => ({
					...prev,
					organizations: prev.organizations.filter((org) => org.id !== organizationId),
					selectedOrganization:
						prev.selectedOrganization?.id === organizationId ? null : prev.selectedOrganization,
					meta: {
						...prev.meta,
						total: prev.meta.total - 1,
					},
					loading: false,
				}));
			} catch (err: any) {
				setError(err.message || 'Failed to delete organization');
				setLoading(false);
			}
		},
		[setLoading, setError]
	);

	return {
		// State
		organizations: state.organizations,
		selectedOrganization: state.selectedOrganization,
		loading: state.loading,
		error: state.error,
		meta: state.meta,
		organisationTypes: state.organisationTypes,
		brands: state.brands,
		timeZones: state.timeZones,
		days: state.days,
		dayParts: state.dayParts,

		// Actions
		fetchOrganizations,
		fetchOrganization,
		fetchOrganisationTypes,
		fetchBrands,
		fetchTimeZones,
		fetchDays,
		fetchDayParts,
		createOrganization,
		updateOrganization,
		deleteOrganization,
		createDayPart,
		updateDayPart,
		deleteDayPart,
		setOrganizations,
		setSelectedOrganization,
		clearError,
		resetState,
	};
};
